import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './EventCard.css';
import { get_event_illustration } from '../../api/functions';
import { useApi } from '../../api/api';

const EventCard = ({ event, index }) => {
  const { t, i18n } = useTranslation();
  const [imageUrl, setImageUrl] = useState(null);
  const getEventImage = useApi(get_event_illustration);
  const startDate = new Date(`${event.event_start_date}T${event.event_start_time}`);
  const endDate = new Date(`${event.event_end_date}T${event.event_end_time}`);
  const isMultiDay = event.event_start_date !== event.event_end_date;

  const langs = {
    'en': 'en-GB',
    'de': 'de-DE',
    'cs': 'cs-CZ'
  };
  const locale = langs[i18n.language] || 'cs-CZ';

  const startDay = String(startDate.getDate()).padStart(2, '0');
  const startMonth = startDate.toLocaleDateString(locale, { month: "short" });
  const endDay = String(endDate.getDate()).padStart(2, '0');
  const endMonth = endDate.toLocaleDateString(locale, { month: "short" });

  const loc = i18n.language === 'cs' ? 'cz' : i18n.language;

  const eventTitle = event[`title_${loc}`] || event.title_cz || event.title_en || event.title_de || 'Untitled';
  const eventText = event[`text_${loc}`] || event.text_cz || event.text_en || event.text_de || '';

  const extractTextFromMarkdown = (markdown) => {
    if (!markdown) return '';
    return markdown
      .replace(/!\[.*?\]\(.*?\)/g, '')
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      .replace(/#{1,6}\s+/g, '')
      .replace(/[*_~`]/g, '')
      .replace(/\n+/g, ' ')
      .trim();
  };

  const eventDescription = extractTextFromMarkdown(eventText);
  const eventTime = `${startDate.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })} - ${endDate.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
  const eventAddress = event.location_name || event.location_address;
  const membersOnly = event.private;

  useEffect(() => {
    if (event.illustration_filename && event.id) {
      getEventImage.execute({ event_id: event.id });
    }
  }, [event.id, event.illustration_filename]);

  useEffect(() => {
    if (getEventImage.data instanceof Blob && !getEventImage.error) {
      const url = URL.createObjectURL(getEventImage.data);
      setImageUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (getEventImage.error) {
      setImageUrl(null);
    }
  }, [getEventImage.data, getEventImage.error]);

  return (
    <div className="event-card-wrapper" style={{ animationDelay: `${(index || 0) * 0.1}s` }}>
      <div className="event-card-rings">
        {[...Array(15)].map((_, i) => (
          <div key={i} className="event-ring-container">
            <div className="event-ring"></div>
          </div>
        ))}
      </div>

      <Link to={`/events/${event.id}`} style={{ textDecoration: 'none' }}>
        <article className="event-card">
          <div className="event-card-tags">
            {isMultiDay ? (
              <div className="event-card-date-tag multi-day">
                <div className="event-date-range-start">
                  <div className="event-date-day">{startDay}</div>
                  <div className="event-date-month">{startMonth}</div>
                </div>
                <div className="event-date-separator">-</div>
                <div className="event-date-range-end">
                  <div className="event-date-day">{endDay}</div>
                  <div className="event-date-month">{endMonth}</div>
                </div>
              </div>
            ) : (
              <div className="event-card-date-tag">
                <div className="event-date-day">{startDay}</div>
                <div className="event-date-month">{startMonth}</div>
              </div>
            )}
            {membersOnly && (
              <div className="event-members-badge">
                <div className="event-members-icon">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="event-member-text">{t('events.membersOnlyMembers')}</span>
                <span className="event-member-text">{t('events.membersOnlyOnly')}</span>
              </div>
            )}
          </div>

          {event.illustration_filename && imageUrl && (
            <div className="event-card-image">
              <img src={imageUrl} alt={eventTitle} loading="lazy" />
            </div>
          )}

          <div className="event-card-content">
            <div className="event-card-meta">
              <div className="event-meta-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>{eventTime}</span>
              </div>
              {eventAddress && (
                <div className="event-meta-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeWidth="2" />
                    <circle cx="12" cy="10" r="3" strokeWidth="2" />
                  </svg>
                  <span>{eventAddress}</span>
                </div>
              )}
            </div>

            <h3 className="event-card-title">{eventTitle}</h3>
            {eventDescription && <p className="event-card-description">{eventDescription}</p>}

            <div className="event-card-button">
              {t('events.learnMore')}
            </div>
          </div>
        </article>
      </Link>
    </div>
  );
};

export default EventCard;
